import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ShoppingCart, Heart, Zap, Minus, Plus } from 'lucide-react';
import { AppContext } from '../App';
import { getImageUrl } from '../utils/image';
import { productUrl } from '../utils/slug';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const QuickViewModal = ({ product, onClose }) => {
  const navigate = useNavigate();
  const { fetchCart, wishlistIds, toggleWishlist, language, convertPrice, currencySymbols, displayCurrency, openCartDrawer } = useContext(AppContext);
  const [quantity, setQuantity] = useState(1);
  const [selectedImg, setSelectedImg] = useState(0);
  const [adding, setAdding] = useState(false);

  if (!product) return null;

  const title = product[`title_${language}`] || product.title;
  const description = product[`description_${language}`] || product.description || '';
  const images = product.images || [];
  const price = product.discounted_price && product.discounted_price < product.price
    ? product.discounted_price : product.price;
  const hasDiscount = product.discounted_price && product.discounted_price < product.price;

  const handleAddToCart = async () => {
    if (adding) return;
    setAdding(true);
    try {
      await axios.post(`${API}/cart/add`, null, {
        params: { product_id: product.id, quantity },
        withCredentials: true,
      });
      await fetchCart();
      if (openCartDrawer) openCartDrawer();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const handleBuyNow = async () => {
    try {
      await axios.post(`${API}/cart/add?product_id=${product.id}&quantity=${quantity}`, {}, { withCredentials: true });
      await fetchCart();
    } catch (_) {}
    onClose();
    navigate('/cart');
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[400]" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[401] w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl">
          <button onClick={onClose} className="absolute top-3 right-3 z-10 p-2 bg-white/80 hover:bg-white rounded-full shadow transition" aria-label="Close">
            <X size={20} />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Images */}
            <div className="p-4">
              <div className="rounded-lg overflow-hidden bg-gray-100 mb-2">
                {images[selectedImg] && (
                  <img
                    src={getImageUrl(images[selectedImg])}
                    alt={title}
                    className="w-full h-64 md:h-80 object-contain"
                  />
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-1 overflow-x-auto">
                  {images.slice(0, 5).map((img, i) => (
                    <button key={i} onClick={() => setSelectedImg(i)}
                      className={`flex-shrink-0 w-14 h-14 rounded border-2 overflow-hidden ${i === selectedImg ? 'border-[#C8A951]' : 'border-transparent'}`}>
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="p-6 flex flex-col">
              <h2 className="text-xl font-bold mb-2 leading-tight">{title}</h2>

              {product.condition && (
                <span className="text-xs text-gray-500 mb-2">{product.condition} {product.year && `• ${product.year}`}</span>
              )}

              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-2xl font-bold" style={{ color: '#B8941E' }}>
                  {currencySymbols[displayCurrency]}{convertPrice(price)}
                </span>
                {hasDiscount && (
                  <span className="text-sm text-gray-400 line-through">
                    {currencySymbols[displayCurrency]}{convertPrice(product.price)}
                  </span>
                )}
              </div>

              {description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">{description}</p>
              )}

              {product.stock > 0 ? (
                <>
                  {product.stock <= 5 && (
                    <p className="text-xs text-orange-600 font-semibold mb-2 animate-pulse">
                      {language === 'tr' ? `Sadece ${product.stock} adet kaldı!` : `Only ${product.stock} left in stock!`}
                    </p>
                  )}

                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center border rounded-lg">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 hover:bg-gray-100 transition"><Minus size={14} /></button>
                      <span className="px-3 text-sm font-medium">{quantity}</span>
                      <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="p-2 hover:bg-gray-100 transition"><Plus size={14} /></button>
                    </div>
                    <button onClick={() => toggleWishlist(product.id)} className="p-2 rounded-lg border hover:bg-red-50 transition" aria-label="Toggle wishlist">
                      <Heart size={18} className={wishlistIds.includes(product.id) ? 'fill-red-500 text-red-500' : 'text-gray-400'} />
                    </button>
                  </div>

                  <div className="flex gap-2 mb-3">
                    <button onClick={handleAddToCart} disabled={adding}
                      className="flex-1 btn-secondary py-2.5 flex items-center justify-center gap-2 disabled:opacity-50">
                      <ShoppingCart size={16} />
                      {adding ? '...' : (language === 'tr' ? 'Sepete Ekle' : 'Add to Cart')}
                    </button>
                    <button onClick={handleBuyNow}
                      className="flex-1 btn-primary py-2.5 flex items-center justify-center gap-2">
                      <Zap size={16} />
                      {language === 'tr' ? 'Hemen Al' : 'Buy Now'}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-red-500 font-semibold mb-3">{language === 'tr' ? 'Stokta Yok' : 'Out of Stock'}</p>
              )}

              <button onClick={() => { onClose(); navigate(productUrl(product)); }}
                className="text-sm text-[#C8A951] hover:underline mt-auto">
                {language === 'tr' ? 'Ürün detaylarını gör →' : 'View full details →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuickViewModal;
